import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import theme from '../theme.config';
const BrandLogo = theme.assets.logo;

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password) {
      toast.error('All fields are required');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const user = await register(name, email, password);
      toast.success(`Welcome aboard, ${user.name}!`);
      navigate('/');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-4 py-8 bg-[#09090b] text-zinc-50 relative">
      <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={BrandLogo} alt={theme.brand.name} className="h-10 w-auto object-contain" />
          </div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-mono text-[10px] text-emerald-400 font-semibold tracking-wider uppercase">Identity Allocation</span>
          </div>
          <h1 className="font-bold text-xl text-white">Create New Account</h1>
          <p className="text-zinc-400 text-xs mt-1">Register to start managing your digital transactions</p>
        </div>

        {/* Card */}
        <form onSubmit={handleSubmit} className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850">
          <div className="space-y-4">
            {[
              { id: 'name', label: 'Full Name', type: 'text', placeholder: 'Enter your name', value: name, setter: setName, testId: 'register-name' },
              { id: 'email', label: 'Email Address', type: 'email', placeholder: 'developer@neurovanta.io', value: email, setter: setEmail, testId: 'register-email' },
            ].map(f => (
              <div key={f.id} className="space-y-2">
                <Label htmlFor={f.id} className="text-xs text-zinc-400 font-medium">{f.label}</Label>
                <Input id={f.id} type={f.type} placeholder={f.placeholder}
                  className="bg-zinc-950 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg"
                  value={f.value} onChange={(e) => f.setter(e.target.value)} data-testid={f.testId} />
              </div>
            ))}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs text-zinc-400 font-medium">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Min. 6 characters"
                  className="bg-zinc-950 border-zinc-800 text-white pr-10 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg"
                  value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-550 hover:text-white"
                  onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs text-zinc-400 font-medium">Confirm Password</Label>
              <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Repeat your password"
                className="bg-zinc-950 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="register-confirm-password" />
            </div>

            <Button type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-5 rounded-lg text-xs tracking-wider"
              disabled={loading} data-testid="register-submit">
              {loading ? (
                <span className="flex items-center gap-2 justify-center">
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Creating account...
                </span>
              ) : <span>Register Now</span>}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-xs">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-medium">Log in here</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
